import {
    createContext,
    isValidElement,
    ReactNode,
    useCallback,
    useContext,
    useLayoutEffect,
    useState,
    useSyncExternalStore,
} from 'react';

export type CompareSide = 'left' | 'right';

// Stands in for a row the other side never rendered, such as an owner only one submission has.
const MISSING_ROW = '\u0000missing';
// Stands in for today's value, which never counts as a difference from the other side.
const LIVE_ROW = '\u0000live';

const createCompareStore = () => {
    const signatures: Record<CompareSide, Map<string, string>> = { left: new Map(), right: new Map() };
    const listeners = new Set<() => void>();
    const notify = () => listeners.forEach((listener) => listener());

    return {
        set: (side: CompareSide, key: string, signature: string) => {
            signatures[side].set(key, signature);
            notify();
        },
        remove: (side: CompareSide, key: string) => {
            signatures[side].delete(key);
            notify();
        },
        get: (side: CompareSide, key: string) => signatures[side].get(key) ?? MISSING_ROW,
        subscribe: (listener: () => void) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
};

type CompareStore = ReturnType<typeof createCompareStore>;

const CompareStoreContext = createContext<CompareStore | null>(null);
const CompareSideContext = createContext<CompareSide>('left');
const CompareCardContext = createContext('');

interface ApplicationCompareProps {
    isComparing: boolean;
    children: ReactNode;
}

// Rows under this compare against the other side only while isComparing, so a lone view highlights nothing.
export const ApplicationCompare = ({ isComparing, children }: ApplicationCompareProps) => {
    const [store] = useState(createCompareStore);

    return (
        <CompareStoreContext.Provider value={isComparing ? store : null}>{children}</CompareStoreContext.Provider>
    );
};

export const CompareSideProvider = CompareSideContext.Provider;
export const CompareCardProvider = CompareCardContext.Provider;

// Raw props rather than rendered text, so a masked value still compares; functions such as handlers are dropped.
const toComparable = (node: unknown): unknown => {
    if (isValidElement(node)) {
        return { type: typeof node.type === 'string' ? node.type : '', props: toComparable(node.props) };
    }
    if (Array.isArray(node)) {
        return node.map(toComparable);
    }
    if (node && typeof node === 'object') {
        return Object.fromEntries(
            Object.entries(node)
                .filter(([, child]) => typeof child !== 'function')
                .map(([name, child]) => [name, toComparable(child)]),
        );
    }
    return node;
};

// ApplicantInfoRow shows every falsy value as the same placeholder, so they compare equal too.
const toSignature = (value: ReactNode) => JSON.stringify(toComparable(value || null));

export const useIsRowChanged = (label: string, value: ReactNode, isLive = false) => {
    const store = useContext(CompareStoreContext);
    const side = useContext(CompareSideContext);
    const card = useContext(CompareCardContext);
    const key = `${card}\u0000${label}`;
    const signature = isLive ? LIVE_ROW : toSignature(value);
    const otherSide: CompareSide = side === 'left' ? 'right' : 'left';

    // A layout effect, so both sides register before paint and an unchanged row never flashes as changed.
    useLayoutEffect(() => {
        if (!store) return undefined;
        store.set(side, key, signature);
        return () => store.remove(side, key);
    }, [store, side, key, signature]);

    const subscribe = useCallback(
        (listener: () => void) => store?.subscribe(listener) ?? (() => undefined),
        [store],
    );
    const otherSignature = useSyncExternalStore(subscribe, () => store?.get(otherSide, key) ?? signature);

    return otherSignature !== signature && signature !== LIVE_ROW && otherSignature !== LIVE_ROW;
};
